import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { MagnifyingGlass, Printer, DownloadSimple, PencilSimple, ClockCounterClockwise, Wrench } from "@phosphor-icons/react";
import { PriorityBadge, PrioritySelect, priorityRank } from "../components/Priority";
import { FaultEffectBadge } from "../components/FaultEffectBadge";
import { submitFaultRepair } from "../lib/faultRepair";
import { fmtDateTime } from "../lib/date";
import { NeedsAttention } from "../components/NeedsAttention";
import { RecordRepairButton } from "../components/RecordRepair";

const emptyFilters = { q: "", priority: "", severity_id: "", status_id: "", category_id: "", asset_type_id: "", assigned_to_id: "", date_from: "", date_to: "" };

export default function FaultsPage() {
  const { hasPerm } = useAuth();
  // Capability-driven visibility: show ONLY the actions the backend will actually accept.
  const canManage = hasPerm("maintenance", "asset_manager", "supervisor", "administrator"); // list column + config filters
  const canAssess = hasPerm("supervisor", "administrator");                 // POST /faults/{id}/assess
  const canEditFault = hasPerm("maintenance", "supervisor", "administrator"); // POST /faults/{id}/edit + PUT
  const canJob = hasPerm("maintenance", "supervisor", "administrator");       // POST /maintenance/jobs
  const canSignoff = hasPerm("supervisor", "administrator");                 // signoff / close-fault
  const [faults, setFaults] = useState([]);
  const [sevs, setSevs] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [cats, setCats] = useState([]);
  const [assetTypes, setAssetTypes] = useState([]);
  const [assetStatuses, setAssetStatuses] = useState([]);
  const [people, setPeople] = useState([]);
  const [filters, setFilters] = useState(emptyFilters);
  const [openOnly, setOpenOnly] = useState(true);
  const [sort, setSort] = useState("default");

  const [sel, setSel] = useState(null);
  const [assess, setAssess] = useState({ assessment: "", status_id: "", asset_status_id: "", priority: "", assigned_to_id: "" });
  const [editSel, setEditSel] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", description: "", notes: "", priority: "medium", reason: "" });
  const [histSel, setHistSel] = useState(null);
  const [history, setHistory] = useState(null);
  const [repairSel, setRepairSel] = useState(null);
  const [repairNotes, setRepairNotes] = useState("");
  const [returnToService, setReturnToService] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const nav = useNavigate();
  const [mtypes, setMtypes] = useState([]);
  const [jobFault, setJobFault] = useState(null);
  const [jobCtx, setJobCtx] = useState([]);
  const [jobRepeat, setJobRepeat] = useState(null);
  const [confirmRepeat, setConfirmRepeat] = useState(false);
  const [jobForm, setJobForm] = useState({ title: "", priority: "medium", maintenance_type_id: "", due_date: "" });

  const params = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
    if (openOnly) p.set("open_only", "true");
    return p.toString();
  }, [filters, openOnly]);

  const load = useCallback(() => { api.get(`/faults?${params}`).then((r) => setFaults(r.data)).catch(() => {}); }, [params]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/config/fault-severities").then((r) => setSevs(r.data)).catch(() => {});
    api.get("/config/fault-categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/config/asset-types").then((r) => setAssetTypes(r.data)).catch(() => {});
    if (canManage) {
      api.get("/config/fault-statuses").then((r) => setStatuses(r.data)).catch(() => {});
      api.get("/config/asset-statuses").then((r) => setAssetStatuses(r.data)).catch(() => {});
      api.get("/config/maintenance-types").then((r) => setMtypes(r.data)).catch(() => {});
      api.get("/people").then((r) => setPeople(r.data)).catch(() => {});
    }
  }, [canManage]);

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const sorted = useMemo(() => {
    const arr = [...faults];
    if (sort === "date") arr.sort((a, b) => (b.reported_at || "").localeCompare(a.reported_at || ""));
    else if (sort === "asset") arr.sort((a, b) => (a.asset_number || "").localeCompare(b.asset_number || ""));
    else if (sort === "status") arr.sort((a, b) => (a.status_name || "").localeCompare(b.status_name || ""));
    else arr.sort((a, b) => (priorityRank(a.priority) - priorityRank(b.priority)) || ((a.final_signoff_at ? 1 : 0) - (b.final_signoff_at ? 1 : 0)));
    return arr;
  }, [faults, sort]);

  const submitAssess = async () => {
    try {
      await api.post(`/faults/${sel.id}/assess`, assess);
      toast.success("Fault assessed"); setSel(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openEdit = (f) => { setEditSel(f); setEditForm({ title: f.title || "", description: f.description || "", notes: f.notes || "", priority: f.priority || "medium", reason: "" }); };
  const submitEdit = async () => {
    try {
      await api.post(`/faults/${editSel.id}/edit`, { title: editForm.title, description: editForm.description, notes: editForm.notes, reason: editForm.reason || null });
      if (editForm.priority !== editSel.priority) await api.put(`/faults/${editSel.id}`, { priority: editForm.priority });
      toast.success("Fault updated"); setEditSel(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openHistory = (f) => {
    setHistSel(f); setHistory(null);
    api.get(`/audit?entity_type=faults&entity_id=${f.id}`).then((r) => setHistory(r.data)).catch(() => setHistory([]));
  };

  const markRepaired = async () => {
    if (repairing) return;                        // guard against a double submit
    setRepairing(true);
    try {
      const { res } = await submitFaultRepair(api, { fault: repairSel, repairNotes, returnToService });
      const d = res.data || {};
      if (returnToService && repairSel.asset_id) {
        if (d.blockers && d.blockers.length) {
          toast.warning(`Fault repaired. Asset NOT returned to service — ${d.blockers.join("; ")}`);
        } else if (d.returned_to_service === false || d.rts_blocked) {
          toast.warning("Fault repaired — return to service is pending approval.");
        } else {
          toast.success("Fault repaired and asset returned to service");
        }
      } else {
        toast.success("Fault marked as repaired");
      }
      setRepairSel(null); setRepairNotes(""); setReturnToService(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setRepairing(false); }
  };

  const exportCsv = async () => {
    try {
      const cp = new URLSearchParams();
      ["q", "priority", "severity_id", "status_id", "category_id", "asset_type_id", "assigned_to_id", "date_from", "date_to"].forEach((k) => { if (filters[k]) cp.set(k, filters[k]); });
      if (openOnly) cp.set("open_only", "true");
      const resp = await api.get(`/reports/faults.csv?${cp.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a"); a.href = url; a.download = "faults.csv"; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };

  const openJob = async (f) => {
    let ctx = { linked_active_job: null, asset_active_jobs: [], completed_jobs: [] };
    try { ctx = (await api.get(`/maintenance/faults/${f.id}/job-context`)).data; } catch { /* fall back to create */ }
    if (ctx.linked_active_job) {
      toast.info(`Already on active job ${ctx.linked_active_job.job_number}`);
      nav(`/maintenance/jobs/${ctx.linked_active_job.id}`);
      return;
    }
    setJobCtx(ctx.asset_active_jobs || []);
    setJobRepeat((ctx.completed_jobs && ctx.completed_jobs.length) ? ctx.completed_jobs[0] : null);
    setConfirmRepeat(false);
    setJobFault(f);
    setJobForm({ title: f.title || f.description?.slice(0, 120) || "Repair fault", priority: f.priority || "medium", maintenance_type_id: "", due_date: "" });
  };
  const addToJob = async (jid) => {
    try {
      await api.post(`/maintenance/jobs/${jid}/add-fault`, { fault_id: jobFault.id });
      toast.success("Fault added to existing job card"); setJobFault(null); nav(`/maintenance/jobs/${jid}`);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.job_id) { toast.info(d.message); nav(`/maintenance/jobs/${d.job_id}`); }
      else toast.error(typeof d === "string" ? d : "Failed to add fault");
    }
  };
  const createJob = async () => {
    try {
      const r = await api.post("/maintenance/jobs", {
        asset_id: jobFault.asset_id, title: jobForm.title, priority: jobForm.priority,
        description: jobFault.description, maintenance_type_id: jobForm.maintenance_type_id || null,
        due_date: jobForm.due_date || null, source_fault_id: jobFault.id,
        confirm_repeat: confirmRepeat,
      });
      toast.success("Job card created from fault"); setJobFault(null); nav(`/maintenance/jobs/${r.data.id}`);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.code === "fault_already_repaired") {
        setJobRepeat({ id: d.job_id, job_number: d.job_number });
        toast.warning(d.message || "This fault was already repaired.");
      } else if (e.response?.status === 409 && d?.job_id) {
        toast.info(d.message || "This fault is already on an active job card.");
        setJobFault(null); nav(`/maintenance/jobs/${d.job_id}`);
      } else toast.error(typeof d === "string" ? d : "Failed to create job");
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3 flex-wrap print:hidden">
        <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">Faults & Defects</h1>
        <div className="flex gap-2">
          <Button data-testid="faults-print" variant="outline" onClick={() => window.print()} className="font-semibold"><Printer size={16} weight="bold" className="mr-1" />Print</Button>
          <Button data-testid="faults-export" variant="outline" onClick={exportCsv} className="font-semibold"><DownloadSimple size={16} weight="bold" className="mr-1" />CSV</Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-2 border-slate-200 rounded-lg p-3 flex flex-col gap-2 print:hidden" data-testid="faults-filters">
        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="fault-search" placeholder="Search asset number, registration or fault text…" value={filters.q} onChange={(e) => setF("q", e.target.value)} className="h-10 pl-8" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <select data-testid="filter-priority" value={filters.priority} onChange={(e) => setF("priority", e.target.value)} className="h-9 border-2 border-slate-300 rounded px-2 text-sm">
            <option value="">All priorities</option><option value="urgent">Urgent</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
          </select>
          <select data-testid="filter-severity" value={filters.severity_id} onChange={(e) => setF("severity_id", e.target.value)} className="h-9 border-2 border-slate-300 rounded px-2 text-sm">
            <option value="">All severities</option>{sevs.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          {canManage && <select data-testid="filter-status" value={filters.status_id} onChange={(e) => setF("status_id", e.target.value)} className="h-9 border-2 border-slate-300 rounded px-2 text-sm">
            <option value="">All statuses</option>{statuses.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>}
          <select data-testid="filter-category" value={filters.category_id} onChange={(e) => setF("category_id", e.target.value)} className="h-9 border-2 border-slate-300 rounded px-2 text-sm">
            <option value="">All categories</option>{cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select data-testid="filter-asset-type" value={filters.asset_type_id} onChange={(e) => setF("asset_type_id", e.target.value)} className="h-9 border-2 border-slate-300 rounded px-2 text-sm">
            <option value="">All asset types</option>{assetTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          {canManage && <select data-testid="filter-assignee" value={filters.assigned_to_id} onChange={(e) => setF("assigned_to_id", e.target.value)} className="h-9 border-2 border-slate-300 rounded px-2 text-sm">
            <option value="">All engineers</option>{people.map((p) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
          </select>}
          <div className="flex items-center gap-1"><label className="text-xs font-semibold text-slate-500">From</label><Input data-testid="filter-date-from" type="date" value={filters.date_from} onChange={(e) => setF("date_from", e.target.value)} className="h-9 text-sm" /></div>
          <div className="flex items-center gap-1"><label className="text-xs font-semibold text-slate-500">To</label><Input data-testid="filter-date-to" type="date" value={filters.date_to} onChange={(e) => setF("date_to", e.target.value)} className="h-9 text-sm" /></div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-700"><input type="checkbox" data-testid="filter-open-only" checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} />Open only</label>
          <div className="flex items-center gap-1 ml-auto">
            <label className="text-xs font-semibold text-slate-500">Sort</label>
            <select data-testid="fault-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="h-9 border-2 border-slate-300 rounded px-2 text-sm">
              <option value="default">Open first, then priority</option><option value="date">Date reported</option><option value="asset">Asset number</option><option value="status">Status</option>
            </select>
          </div>
          <Button data-testid="filter-clear" variant="outline" onClick={() => { setFilters(emptyFilters); setOpenOnly(true); }} className="h-9 text-xs font-semibold">Clear</Button>
        </div>
      </div>

      {/* Printable heading */}
      <h2 className="hidden print:block font-display text-2xl font-black">TMIC Ops — Faults Report</h2>
      {canJob && <div className="print:hidden"><NeedsAttention /></div>}

      <div className="flex flex-col gap-2" data-testid="faults-list">
        {sorted.map((f) => (
          <div key={f.id} data-testid={`fault-row-${f.id}`} className="bg-white border-2 border-slate-200 rounded-lg p-4 flex items-start justify-between gap-3 print:border print:break-inside-avoid">
            <div className="min-w-0">
              {(f.asset_number || f.asset_name) && (
                <p className="font-body text-xs font-bold uppercase tracking-wide text-blue-700">
                  {f.asset_number ? `#${f.asset_number} ` : ""}{f.asset_name}{f.asset_registration ? ` · ${f.asset_registration}` : ""}
                </p>
              )}
              <div className="flex items-center gap-2 flex-wrap"><FaultEffectBadge effect={f.operational_effect} testid={`fault-effect-${f.id}`} /><PriorityBadge priority={f.priority} testid={`fault-priority-${f.id}`} /><span className="font-body font-semibold text-slate-800">{f.title || f.description}</span></div>
              {f.title && f.description && <p className="text-sm text-slate-600 mt-0.5">{f.description}</p>}
              {f.notes && <p className="text-xs text-slate-500 mt-0.5 italic">Notes: {f.notes}</p>}
              <p className="font-mono text-xs text-slate-500 mt-1">
                {f.severity_name ? `${f.severity_name} · ` : ""}{f.status_name || (f.final_signoff_at ? "Closed" : "Open")}
                {f.reporter_name ? ` · by ${f.reporter_name}` : ""} · {fmtDateTime(f.reported_at)}
                {f.assignee_name ? ` · assigned ${f.assignee_name}` : ""}{f.repair_status ? ` · ${f.repair_status}` : ""}
              </p>
              {f.asset_id && <Link to={`/assets/${f.asset_id}`} className="text-xs text-blue-600 font-semibold print:hidden">View asset →</Link>}
              {f.jobs?.length > 0 && <Link to={`/maintenance/jobs/${f.jobs[0].id}`} data-testid={`fault-job-link-${f.id}`} title={f.jobs.map((j) => `${j.job_number || "Job"} · ${j.title || "Job"} — ${j.status}${j.resolution_status ? ` (${j.resolution_status})` : ""}`).join("\n")} className="ml-2 inline-flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-300 rounded px-2 py-0.5 print:hidden"><Wrench size={12} weight="bold" />{f.jobs[0].job_number || "Job"}{f.jobs.length > 1 ? ` +${f.jobs.length - 1}` : ""}</Link>}
            </div>
            {canManage && (
              <div className="flex flex-col gap-2 shrink-0 print:hidden">
                {f.asset_id && !f.final_signoff_at && <RecordRepairButton assetId={f.asset_id} faultId={f.id} testid={`record-repair-${f.id}`} onDone={load} className="h-8 text-xs" />}
                {canAssess && !f.final_signoff_at && <Button data-testid={`assess-${f.id}`} onClick={() => { setSel(f); setAssess({ assessment: "", status_id: "", asset_status_id: "", priority: f.priority || "", assigned_to_id: f.assigned_to_id || "" }); }} className="bg-slate-900 hover:bg-slate-800 font-semibold h-8 text-xs">Assess</Button>}
                {canEditFault && <Button data-testid={`edit-fault-${f.id}`} variant="outline" onClick={() => openEdit(f)} className="font-semibold h-8 text-xs"><PencilSimple size={13} className="mr-1" />Edit</Button>}
                {canJob && f.asset_id && !f.final_signoff_at && <Button data-testid={`create-job-${f.id}`} variant="outline" onClick={() => openJob(f)} className="font-semibold h-8 text-xs"><Wrench size={13} className="mr-1" />{f.jobs?.length ? "Another job" : "Job Card"}</Button>}
                <Button data-testid={`history-fault-${f.id}`} variant="outline" onClick={() => openHistory(f)} className="font-semibold h-8 text-xs"><ClockCounterClockwise size={13} className="mr-1" />History</Button>
                {canSignoff && !f.final_signoff_at && <Button data-testid={`repair-${f.id}`} onClick={() => { setRepairSel(f); setRepairNotes(""); setReturnToService(false); }} className="bg-emerald-600 hover:bg-emerald-700 font-semibold h-8 text-xs">Mark Repaired</Button>}
              </div>
            )}
          </div>
        ))}
        {sorted.length === 0 && <p className="text-slate-400">No faults match these filters.</p>}
      </div>

      {/* Assess */}
      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-display">Assess Fault</DialogTitle>
            <DialogDescription>Record your assessment, set priority/assignee and optionally update the fault or asset status.</DialogDescription>
          </DialogHeader>
          <Textarea data-testid="assess-notes" placeholder="Assessment" value={assess.assessment} onChange={(e) => setAssess({ ...assess, assessment: e.target.value })} />
          <label className="font-body text-sm font-semibold">Priority</label>
          <PrioritySelect testid="assess-priority" value={assess.priority || "medium"} onChange={(v) => setAssess({ ...assess, priority: v })} className="w-full" />
          <label className="font-body text-sm font-semibold">Assign engineer</label>
          <select data-testid="assess-assignee" className="h-11 border-2 border-slate-300 rounded px-2" value={assess.assigned_to_id} onChange={(e) => setAssess({ ...assess, assigned_to_id: e.target.value })}>
            <option value="">Unassigned…</option>{people.map((p) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
          </select>
          <label className="font-body text-sm font-semibold">Fault workflow status</label>
          <select data-testid="assess-status" className="h-11 border-2 border-slate-300 rounded px-2" value={assess.status_id} onChange={(e) => setAssess({ ...assess, status_id: e.target.value })}>
            <option value="">Keep current…</option>{statuses.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <label className="font-body text-sm font-semibold">Asset operational status (optional)</label>
          <select data-testid="assess-asset-status" className="h-11 border-2 border-slate-300 rounded px-2" value={assess.asset_status_id} onChange={(e) => setAssess({ ...assess, asset_status_id: e.target.value })}>
            <option value="">No change…</option>{assetStatuses.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <Button data-testid="assess-submit" onClick={submitAssess} className="bg-blue-600 hover:bg-blue-700 font-semibold">Save Assessment</Button>
        </DialogContent>
      </Dialog>

      {/* Edit wording */}
      <Dialog open={!!editSel} onOpenChange={(o) => !o && setEditSel(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-display">Edit Fault</DialogTitle>
            <DialogDescription>The current wording is shown here. Previous wording is preserved in the fault history.</DialogDescription>
          </DialogHeader>
          <label className="font-body text-sm font-semibold">Title</label>
          <Input data-testid="edit-fault-title" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} />
          <label className="font-body text-sm font-semibold">Description</label>
          <Textarea data-testid="edit-fault-description" value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} rows={3} />
          <label className="font-body text-sm font-semibold">Notes</label>
          <Textarea data-testid="edit-fault-notes" value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} rows={2} />
          <label className="font-body text-sm font-semibold">Priority</label>
          <PrioritySelect testid="edit-fault-priority" value={editForm.priority} onChange={(v) => setEditForm({ ...editForm, priority: v })} className="w-full" />
          <label className="font-body text-sm font-semibold">Reason for correction (optional)</label>
          <Input data-testid="edit-fault-reason" placeholder="e.g. clarified which side" value={editForm.reason} onChange={(e) => setEditForm({ ...editForm, reason: e.target.value })} />
          <Button data-testid="edit-fault-submit" onClick={submitEdit} className="bg-blue-600 hover:bg-blue-700 font-semibold">Save Changes</Button>
        </DialogContent>
      </Dialog>

      {/* History */}
      <Dialog open={!!histSel} onOpenChange={(o) => !o && setHistSel(null)}>
        <DialogContent className="bg-white max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Fault History</DialogTitle>
            <DialogDescription>Corrections and changes to this fault, most recent first.</DialogDescription>
          </DialogHeader>
          {history === null ? <p className="text-slate-400 text-sm">Loading…</p> : history.length === 0 ? <p className="text-slate-400 text-sm" data-testid="fault-history-empty">No recorded changes.</p> : (
            <div className="flex flex-col gap-2" data-testid="fault-history-list">
              {history.map((a) => (
                <div key={a.id} className="border-2 border-slate-200 rounded p-2 text-sm">
                  <p className="font-mono text-xs text-slate-500">{fmtDateTime(a.when)} · {a.who || "System"} · {a.action_label || a.action}</p>
                  {a.reason && <p className="text-xs text-amber-700 mt-0.5">Reason: {a.reason}</p>}
                  {a.changes?.length > 0 ? (
                    <div className="mt-1 flex flex-col gap-1">
                      {a.changes.map((c, i) => (
                        <div key={i} className="text-xs"><b className="text-slate-600">{c.field}:</b> <span className="line-through text-slate-400">{String(c.old ?? "—")}</span> → <span className="text-slate-800">{String(c.new ?? "—")}</span></div>
                      ))}
                    </div>
                  ) : (a.lines?.length > 0 && <div className="mt-1 text-xs text-slate-600">{a.lines.join(" · ")}</div>)}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Repair */}
      <Dialog open={!!repairSel} onOpenChange={(o) => !o && setRepairSel(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-display">Mark Fault as Repaired</DialogTitle>
            <DialogDescription>This signs off the fault and removes it from the open faults list.</DialogDescription>
          </DialogHeader>
          <p className="font-body text-sm text-slate-600">{repairSel?.title || repairSel?.description}</p>
          <label className="font-body text-sm font-semibold">Repair notes (optional)</label>
          <Textarea data-testid="repair-notes" placeholder="What was done to repair this?" value={repairNotes} onChange={(e) => setRepairNotes(e.target.value)} />
          {repairSel?.asset_id && (
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              <input type="checkbox" data-testid="repair-rts" checked={returnToService} onChange={(e) => setReturnToService(e.target.checked)} />
              Return this asset to service
            </label>
          )}
          <Button data-testid="repair-submit" onClick={markRepaired} disabled={repairing} className="bg-emerald-600 hover:bg-emerald-700 font-semibold">{repairing ? "Working…" : "Confirm Repaired"}</Button>
        </DialogContent>
      </Dialog>

      {/* Create job card from fault */}
      <Dialog open={!!jobFault} onOpenChange={(o) => !o && setJobFault(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-display">Create Maintenance Job Card</DialogTitle>
            <DialogDescription>Raise a maintenance job from this fault. The job is linked back to the fault, so you don't have to allocate it again inside the work order.</DialogDescription>
          </DialogHeader>
          <p className="font-body text-sm text-slate-600">{jobFault?.asset_number ? `#${jobFault.asset_number} · ` : ""}{jobFault?.title || jobFault?.description}</p>
          {jobRepeat && (
            <div data-testid="fault-already-repaired" className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3">
              <div className="font-body text-sm font-semibold text-amber-900 mb-1">This fault was already repaired.</div>
              <div className="font-body text-xs text-amber-800">
                A repair job (<Link to={`/maintenance/jobs/${jobRepeat.id}`} data-testid="open-repaired-job" className="underline font-bold">{jobRepeat.job_number}</Link>) has already been completed for this fault. Only raise another job card if genuinely <span className="font-semibold">additional / further</span> work is needed.
              </div>
              <label className="flex items-center gap-2 mt-2 text-xs font-semibold text-amber-900 cursor-pointer">
                <input type="checkbox" data-testid="confirm-repeat-job" checked={confirmRepeat} onChange={(e) => setConfirmRepeat(e.target.checked)} />
                Yes, raise a new job card for further work on this fault
              </label>
            </div>
          )}
          {jobCtx.length > 0 && (
            <div data-testid="add-to-existing-job" className="rounded-lg border-2 border-blue-200 bg-blue-50/50 p-3">
              <div className="font-body text-sm font-semibold text-slate-800 mb-1">This asset already has an active job card. Add this fault to it?</div>
              <div className="flex flex-col gap-1">
                {jobCtx.map((j) => (
                  <Button key={j.id} data-testid={`add-to-job-${j.id}`} onClick={() => addToJob(j.id)} variant="outline" className="justify-start font-semibold h-9 text-xs">
                    <Wrench size={13} className="mr-1" />Add to {j.job_number} — {j.title || "Job"} ({j.status})
                  </Button>
                ))}
              </div>
              <div className="text-[11px] text-slate-500 mt-2">…or create a separate job card below.</div>
            </div>
          )}
          <label className="font-body text-sm font-semibold">Job title</label>
          <Input data-testid="job-card-title" value={jobForm.title} onChange={(e) => setJobForm({ ...jobForm, title: e.target.value })} />
          <label className="font-body text-sm font-semibold">Priority</label>
          <PrioritySelect testid="job-card-priority" value={jobForm.priority} onChange={(v) => setJobForm({ ...jobForm, priority: v })} className="w-full" />
          <label className="font-body text-sm font-semibold">Maintenance type (optional)</label>
          <select data-testid="job-card-type" className="h-11 border-2 border-slate-300 rounded px-2" value={jobForm.maintenance_type_id} onChange={(e) => setJobForm({ ...jobForm, maintenance_type_id: e.target.value })}>
            <option value="">None…</option>{mtypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <label className="font-body text-sm font-semibold">Due date (optional)</label>
          <Input data-testid="job-card-due" type="date" value={jobForm.due_date} onChange={(e) => setJobForm({ ...jobForm, due_date: e.target.value })} />
          <Button data-testid="job-card-create" onClick={createJob} disabled={!jobForm.title || (jobRepeat && !confirmRepeat)} className="bg-blue-600 hover:bg-blue-700 font-semibold">Create Job Card</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
